// ─── REVCENTRIQ DESIGN TOKENS ─────────────────────────────────────────────────
// All brand colors, typography, and shared CSS.
// The iQ blue is the primary brand color — used in the logo and all iQ suffixes.

// ── Brand Colors ──────────────────────────────────────────────────────────────
export const COLORS = {
  // Core brand
  iqBlue: "#2563eb",          // The iQ blue — logo, buttons, focus states
  iqBlueMid: "#3b82f6",       // Lighter blue for gradients
  iqBlueDark: "#1d4ed8",      // Darker blue for gradients

  // Backgrounds
  bgBase: "#050810",          // Page background
  bgCard: "#0a0e1a",          // Card background
  bgElevated: "#0d1120",      // Input / pill background
  bgSection: "#07091266",     // Subtle section tint

  // Borders
  border: "#1e2438",          // Default border
  borderLight: "#0d1120",     // Subtle border (nav bottom)

  // Text
  textPrimary: "#e2e8f0",     // Headings, primary content
  textSecondary: "#8892b0",   // Body text, labels
  textMuted: "#4a5568",       // Placeholders, footer text
  textMono: "#60a5fa",        // Monospace accents

  // Semantic
  success: "#059669",
  successBg: "#05996918",
  error: "#f87171",
  errorBg: "#e11d4812",
  errorBorder: "#e11d4830",
};

// ── Typography ────────────────────────────────────────────────────────────────
export const FONTS = {
  display: "'Syne', sans-serif",      // Headings, logo, KPI numbers
  body: "'DM Sans', sans-serif",      // Body text, buttons, nav
  mono: "'DM Mono', monospace",       // Labels, badges, code
  googleImport: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&family=DM+Mono:wght@400;500&family=Syne:wght@600;700;800&display=swap",
};

// ── Global CSS ────────────────────────────────────────────────────────────────
// Injected once via GlobalStyles component.
export const GLOBAL_CSS = `
@import url('${FONTS.googleImport}');

* { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }

::-webkit-scrollbar { width: 5px; }
::-webkit-scrollbar-track { background: ${COLORS.bgBase}; }
::-webkit-scrollbar-thumb { background: #2a2f45; border-radius: 3px; }

.fade-up { opacity: 0; transform: translateY(24px); transition: opacity .6s ease, transform .6s ease; }
.fade-up.visible { opacity: 1; transform: translateY(0); }

@keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
@keyframes pulse-ring { 0%{transform:scale(1);opacity:.6} 100%{transform:scale(1.5);opacity:0} }
@keyframes spin-slow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }

.mod-pill {
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 16px 18px;
  cursor: default;
  transition: all .2s;
  background: ${COLORS.bgElevated};
}
.mod-pill:hover { transform: translateY(-3px); }

.pricing-card {
  border-radius: 16px;
  padding: 32px;
  border: 1px solid ${COLORS.border};
  background: ${COLORS.bgCard};
  transition: transform .2s, border-color .2s;
  flex: 1;
  min-width: 260px;
}
.pricing-card:hover { transform: translateY(-4px); }
.pricing-card.popular {
  border-color: ${COLORS.iqBlue}55;
  background: linear-gradient(160deg,#0d1526,${COLORS.bgCard});
}

.nav-link {
  color: ${COLORS.textSecondary};
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;
  transition: color .15s;
  background: none;
  border: none;
  font-family: ${FONTS.body};
}
.nav-link:hover { color: ${COLORS.textPrimary}; }

.btn-primary {
  background: linear-gradient(135deg, ${COLORS.iqBlue}, ${COLORS.iqBlueMid});
  color: #fff;
  border: none;
  border-radius: 10px;
  font-family: ${FONTS.body};
  font-weight: 600;
  font-size: 15px;
  padding: 13px 28px;
  cursor: pointer;
  transition: opacity .2s, transform .15s;
  box-shadow: 0 4px 24px ${COLORS.iqBlue}44;
}
.btn-primary:hover { opacity: .92; transform: translateY(-1px); }
.btn-primary:active { transform: translateY(0); }

.btn-ghost {
  background: none;
  color: ${COLORS.textSecondary};
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
  font-family: ${FONTS.body};
  font-weight: 500;
  font-size: 14px;
  padding: 11px 24px;
  cursor: pointer;
  transition: all .15s;
}
.btn-ghost:hover { border-color: ${COLORS.iqBlue}44; color: ${COLORS.textPrimary}; }

.input-field {
  width: 100%;
  background: ${COLORS.bgElevated};
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
  padding: 13px 16px;
  color: ${COLORS.textPrimary};
  font-family: ${FONTS.body};
  font-size: 14px;
  outline: none;
  transition: border-color .15s;
}
.input-field:focus {
  border-color: ${COLORS.iqBlue};
  box-shadow: 0 0 0 3px ${COLORS.iqBlue}18;
}
.input-field::placeholder { color: ${COLORS.textMuted}; }
`;

// ── Demo credentials ──────────────────────────────────────────────────────────
// Loaded from .env — never hardcoded here. Add to Vercel env vars for production.
export const DEMO_USER = {
  email: import.meta.env.VITE_DEMO_EMAIL,
  password: import.meta.env.VITE_DEMO_PASSWORD,
  name: "Alex Rivera",
  company: "Acme Corp",
  role: "CRO",
};
